import { Icon, Stack, Text, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { Currency } from '@hotjar/product-catalog';
import { useState } from 'react';
import { Discount } from './Discount';
import type { DiscountType, BundleDeductionState } from './Discount';
import { usePrettifyPrice } from '~/subscription-management/utils/cost/prettify-price';

export interface DiscountsType {
  type: DiscountType;
  state?: BundleDeductionState;
  amount?: number;
  percentage?: number;
}

const S = {
  Row: styled(Stack)`
    justify-content: space-between;
  `,
  AccordionButton: styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: ${tokens.ALIAS_TEXT_COLORS.text_light};
    background: none;
    border: none;
    padding: 0;
  `,
  AccordionChevron: styled(Icon)<{ isOpen: boolean }>`
    min-width: ${tokens.spacer.x1pt5};
    width: ${tokens.spacer.x1pt5};
    transform: ${(p) => (p.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
    ${tokens.transition()};
  `,
  AccordionTitle: styled.span`
    display: flex;
    align-items: center;
    gap: ${tokens.spacer.x0pt5};
  `,
  AccordionContent: styled.div`
    padding-top: ${tokens.spacer.x1};
    padding-left: ${tokens.spacer.x2pt5};
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacer.x0};
    margin-bottom: ${tokens.spacer.x1};
  `,
};

interface Props {
  discounts: DiscountsType[];
  currency: Currency;
}

export function Discounts({ discounts, currency }: Props): JSX.Element {
  const { t } = useTranslationWithPrefix('checkout:subscriptions.cost.discounts');
  const { prettifyPrice } = usePrettifyPrice(currency);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const allDiscounts = discounts.map((discount) => (
    <S.Row key={discount.type}>
      <Discount
        discount={discount.type}
        state={discount.state ?? 'applied'}
        noIcon={discounts.length !== 1}
        percentage={discount.percentage}
      />
      {discount.amount !== undefined && (
        <Text textType="caption" textColor="text_light">
          {prettifyPrice(discount.amount)}
        </Text>
      )}
    </S.Row>
  ));

  if (discounts.length === 1) {
    return <>{allDiscounts}</>;
  }

  return (
    <>
      <S.AccordionButton onClick={() => setIsOpen(!isOpen)}>
        <S.AccordionTitle>
          <Icon icon="special_offer" />
          <Text textType="caption" textColor="text_light">
            {t('account')}
          </Text>
          <S.AccordionChevron icon="chevron_bottom" isOpen={isOpen} />
        </S.AccordionTitle>
      </S.AccordionButton>
      {isOpen && <S.AccordionContent>{allDiscounts}</S.AccordionContent>}
    </>
  );
}
